import React, { FC, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ISelectOption } from 'components/forms/Selects/SelectOption'
import pick from 'lodash/pick'
import { useGlobalToast } from 'transfix-ui/components/Toast/GlobalToast'
import { Box } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { STOP_TYPE } from 'types'
import {
  Shipment,
  useUpdateCarrierActiveShipment,
  ShipmentStop,
} from 'apps/carrier/apiTypes/activeShipments'
import ShipmentFields from './ShipmentFields'
import ShipmentForm from './ShipmentForm'
import StopFields from './StopFields'
import { ActiveShipmentsSchemaType } from './schema/ActiveShipmentsSchema'

interface IShipmentRowProps {
  shipment: Shipment
  driverList: ISelectOption<number>[]
}

/**
 * Get stop attributes request data
 * @param stops
 * @returns update object
 */
const getStopAttributes = (stops?: ShipmentStop[]) =>
  stops?.map(stop => pick(stop, ['arrivedAt', 'leftAt', 'manualEta', 'id', 'trailerEmptiedAt']))

// Shipment Row in default + edit state
export const ShipmentRow: FC<IShipmentRowProps> = ({ driverList, shipment: currentShipment }) => {
  const { openToast } = useGlobalToast()
  const [isEdit, setIsEdit] = useState(false)
  const [isSaved, setisSaved] = useState(false)
  const [shipment, setShipment] = useState<Shipment>(currentShipment)

  const defaultValues = {
    driverId: shipment.driver?.id,
    trailerNumber: shipment.trailerNumber,
    truckNumber: shipment.truckNumber,
    vin: shipment.vin,
    pickupsAttributes: getStopAttributes(shipment.pickups),
    deliveriesAttributes: getStopAttributes(shipment.deliveries),
  }
  const { id } = useParams<{ id: string }>()
  const {
    error,
    loading: updateLoading,
    mutate: updateActiveShipment,
  } = useUpdateCarrierActiveShipment({
    carrierId: Number(id),
    shipmentId: Number(shipment.id),
  })

  const { data: messages }: any = error || {}

  const successHandler = (data: ActiveShipmentsSchemaType) => {
    updateActiveShipment(data).then(resp => {
      setShipment(resp)
      openToast({
        toastMessage: `Shipment #${shipment.id} Updated!`,
        toastType: 'default',
      })
      setIsEdit(false)
      setTimeout(() => setisSaved(true), 300)
    })
  }

  const lastStopIndex = (shipment.deliveries?.length || 1) - 1

  return (
    <Box
      className={`shipment-row--box ${isSaved ? 'isSaved' : ''}`}
      role='rowgroup'
      borderTop={1}
      borderColor={brandColors.coolGray4}
      data-test={`shipment-row-${shipment.id}`}
      bgcolor={isEdit ? brandColors.coolGray1 : brandColors.white}>
      <ShipmentForm
        onFormSubmit={successHandler}
        defaultValues={defaultValues}
        apiErrors={messages?.errors}
        isEditMode={isEdit}
        isLoading={updateLoading}
        onSetEditMode={setIsEdit}>
        <ShipmentFields driverList={driverList} shipment={shipment} />
        {/* SECTION - Shipment Stops: Pickup + Delivery */}
        <Box width='70%' px={3} borderLeft={1} borderColor={brandColors.coolGray4}>
          {shipment.pickups?.map(pickup => {
            const isFirstPickup =
              ['assigned', 'covered'].includes(shipment.status?.key || '') &&
              pickup.stopSequence === 1
            return (
              <StopFields
                type={STOP_TYPE.pickup}
                stop={pickup}
                key={pickup.stopSequence}
                isEtaRequired={isFirstPickup}
              />
            )
          })}
          {shipment.deliveries?.map((delivery, index) => (
            <StopFields
              type={STOP_TYPE.delivery}
              stop={delivery}
              key={delivery.stopSequence}
              isLastStop={lastStopIndex === index}
            />
          ))}
        </Box>
      </ShipmentForm>
    </Box>
  )
}
